import React, { useState } from 'react';
import styled from 'styled-components';
import { Box, Button, H2, Panel, Text, ProgressCircle, Table, Modal, Small, Link, Flex, FlexItem, Dropdown, Select, Tooltip, Switch } from '@bigcommerce/big-design';
import { MoreHorizIcon } from '@bigcommerce/big-design-icons';
import { Link as BrowserLink, useLocation, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { deleteReview, updateReview } from '../reviewsSlice';
import StarRating from './StarRating';
import ImageFeaturedReviews1 from '../images/featured-reviews-1.png';
import ImageFeaturedReviews2 from '../images/featured-reviews-2.png';

const StatusSelectWrapper = styled.div`
    [data-value="approved"] {
        color: ${({ theme }) => theme.colors.success};
    }
    [data-value="pending"] {
        color: ${({ theme }) => theme.colors.danger};
    }
    [data-value="disapproved"] {
        color: ${({ theme }) => theme.colors.secondary};
    }
`;

/**
 * Component hiển thị danh sách reviews với các chức năng quản lý
 * @param {Object} reviews - Dữ liệu reviews từ store
 * @param {string} baseUrl - URL gốc để navigation
 */
const Reviews = ({ reviews, baseUrl }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const loading = useSelector(state => state.reviews.loading);
    const query = new URLSearchParams(useLocation().search);
    const storeHash = useSelector(state => state.auth.data.store_hash);
    const [openModalFeaturedReviews, setOpenModalFeaturedReviews] = useState(false);
    const { data: settings } = useSelector(state => state.settings);

    const goToPage = (page, limit) => {
        if (page) {
            query.set('page', page)
        }
        if (limit) {
            query.set('limit', limit);
        }
        history.push(`?${query.toString()}`);
    };

    const loaded = reviews && reviews.data && reviews.meta && reviews.meta.pagination;

    // Hàm hiển thị tóm tắt criteria ratings
    const renderCriteriaRatings = (item) => {
        if (!settings?.enableReviewCriteria || !settings?.reviewCriteria || !item.criteriaRatings) {
            return '';
        }

        return (
            <Flex flexDirection="column" style={{gap: '2px'}}>
                {settings.reviewCriteria
                    .filter(criterion => criterion.enabled && item.criteriaRatings[criterion.id] !== undefined)
                    .slice(0, 3) // Hiển thị tối đa 3 tiêu chí
                    .map(criterion => {
                        const rating = item.criteriaRatings[criterion.id];
                        const label = criterion.label.replace(/[🔧💰⚡🎨👍🚚📦✅👌🔄]/g, '').trim();

                        return (
                            <Small key={criterion.id} color="secondary60" margin="none">
                                {criterion.type === 'rating'
                                    ? <span>{label}: <StarRating rating={rating} small /></span>
                                    : `${label}: ${rating ? '✅' : '❌'}`
                                }
                            </Small>
                        );
                    })}
                {settings.reviewCriteria.filter(criterion => criterion.enabled && item.criteriaRatings[criterion.id] !== undefined).length > 3 && (
                    <Small color="secondary40">...</Small>
                )}
            </Flex>
        );
    };

    return (
        <div className={loading === 'pending' ? 'app-panel-loading' : ''}>
            <Panel>
                {loaded ?
                    <>
                        <Flex marginBottom='small' justifyContent='flex-end'>
                            <Box style={{cursor: 'pointer'}} onClick={()=>setOpenModalFeaturedReviews(true)}>Featured Reviews ?</Box>
                        </Flex>
                        <Flex alignItems="center" justifyContent="space-between" marginBottom="large">
                            <FlexItem>
                                <H2 margin="none">Reviews</H2>
                            </FlexItem>
                            <FlexItem>
                                <Button variant="primary" onClick={() => history.push(`${baseUrl}&route=edit`)}>Add Review</Button>
                            </FlexItem>
                        </Flex>
                        <Table
                            columns={[
                                {header: 'ID', hash: 'reviewId', render: item => item.reviewId || '' },
                                {header: 'Title', hash: 'title', render: item => <Link href={`https://store-${storeHash}.mybigcommerce.com/products.php?productId=${item.productId}`} target="_blank">{item.title}</Link> || '' },
                                {header: 'Name', hash: 'name', render: item => item.email ? <Link href={`mailto:${item.email}`} target="_blank">{item.name || item.email}</Link> : (item.name || '') },
                                {header: 'Rating', hash: 'rating', width: 250, render: item => (
                                    <Flex flexDirection="column" style={{gap: '4px'}}>
                                        <div>
                                            <StarRating rating={item.rating} />
                                        </div>
                                        {renderCriteriaRatings(item)}
                                    </Flex>
                                )},
                                {header: 'Status', hash: 'status', width: 150, render: item => item.status ?
                                    <StatusSelectWrapper>
                                        <Select
                                            data-value={item.status}
                                            value={item.status}
                                            filterable={false}
                                            onOptionChange={val => dispatch(updateReview({
                                                reviewId: item.reviewId,
                                                productId: item.productId,
                                                status: val
                                            }))}
                                            options={[
                                                { value: 'approved', content: 'Approved' },
                                                { value: 'pending', content: 'Pending' },
                                                { value: 'disapproved', content: 'Disapproved' },
                                            ]} />
                                    </StatusSelectWrapper> : ''
                                },
                                {header: 'Images', hash: 'images', width: 100, render: item =>
                                    item.images.length > 0
                                        ? <BrowserLink to={`${baseUrl}&route=manageimages&reviewId=${item.reviewId}`} title="Manage Images"><img src={item.images[0].thumbUrl} alt="" /></BrowserLink>
                                        : '' },
                                {header: 'Featured', hash: 'featured', render: item => (
                                    <Tooltip
                                        placement="bottom-end"
                                        trigger={
                                            <Switch
                                                checked={item.isFeatured}
                                                onChange = {
                                                    () => dispatch(updateReview({
                                                        reviewId: item.reviewId,
                                                        productId: item.productId,
                                                        isFeatured: !(item.isFeatured),
                                                    }))
                                                }
                                                disabled={item.status !== 'approved'}
                                            />
                                        }
                                    >
                                        Only approved reviews can be set as featured reviews.
                                    </Tooltip>
                                )},
                                {header: 'Action', hash: 'action',
                                    render: item =>
                                        <Dropdown
                                            items={[
                                                { content: 'Manage Images', onItemClick: () => history.push(`${baseUrl}&route=manageimages&reviewId=${item.reviewId}`) },
                                                { content: 'Edit', onItemClick: () => history.push(`${baseUrl}&route=edit&reviewId=${item.reviewId}`) },
                                                /*{ content: 'Edit', type: 'link', url: `https://store-${storeHash}.mybigcommerce.com/manage/products/product-reviews/${item.reviewId}/edit`, target: '_blank' },*/
                                                { content: 'Delete', onItemClick: () => dispatch(deleteReview(item.reviewId)), actionType: 'destructive' },
                                            ]}
                                            toggle={<Button variant="subtle"><MoreHorizIcon /></Button>}
                                        />}
                            ]}
                            items={reviews.data}
                            keyField='reviewId'
                            pagination={{
                                currentPage: reviews.meta.pagination.page,
                                totalItems: reviews.meta.pagination.total,
                                onPageChange: page => goToPage(page, null),
                                onItemsPerPageChange: limit => goToPage(null, limit),
                                itemsPerPage: reviews.meta.pagination.limit,
                                itemsPerPageOptions: [5, 10, 20, 50, 100],
                            }}
                            stickyHeader
                        />
                        <Modal
                            header="What is Featured Reviews ?"
                            actions={[
                                { text: 'OK', onClick: () => setOpenModalFeaturedReviews(false) },
                            ]}
                            isOpen={openModalFeaturedReviews}
                            onClose={() => setOpenModalFeaturedReviews(false)}
                        >
                                <Text bold marginBottom='none'>Description:</Text>
                                <Text>- The Featured Reviews section displays a list of customer reviews that have been marked as featured on your storefront.</Text>
                                <Text bold marginBottom='none'>How it works:</Text>
                                <Text>- To use this feature, simply insert a <span style={{fontWeight: 'bold'}}>{'<featured-reviews>'}</span> tag into your page using the HTML widget in the Page Builder, or by any other method.</Text>
                                <Text>- The app will detect this tag and automatically load the featured reviews into that position on the page.</Text>
                                <Text bold marginBottom='none'>Example usage:</Text>
                                <Text>{'<featured-reviews></featured-reviews>'}</Text>
                                <Flex flexDirection='row' style={{gap: '24px'}}>
                                    <img src={ImageFeaturedReviews1} alt='Image Featured Reviews 1' style={{flex: 1}} />
                                    <img src={ImageFeaturedReviews2} alt='Image Featured Reviews 2' style={{flex: 1}}/>
                                </Flex>
                        </Modal>
                    </> : ''}
                {loading === 'pending' ? <div className="app-loading-indicator"><ProgressCircle size="large" /></div> : ''}
            </Panel>
        </div>
    );
}

export default Reviews;
